import { useMemo } from 'react'
import { RateConvert, useRateConvert, useTickerBatch } from '@web/walter-lib/stream'
import { GetCurrenciesReturn, GetRatesBatchReturn, StakingUser } from '@web/walter-lib/api'
import { useCommonCMSConfig, useCurrencies, useStakingCurrencies, useStakingUser } from '~api'
import { CoinsTableRowData } from '~components/CoinsTable/CoinsTablePure'
import { SymbolInfo } from '~hooks/useBlocksData'
import { MarketAssetFilter } from '~pages/MarketPage/AllCoins/Filters/useFilters'
import { useBaseCurrency } from '~hooks/useBaseCurrency'
import { generateUrl } from '~utils/navigation/generateUrl'
import { Routes } from '~constants/routes'
import { useRatesDataMap } from '~hooks/useRatesMap'
import { isCoinInSymbolsFilter, makeFilterByCodeName } from '~utils/filters'
import { usePrevCurrentRatesBatch } from '~hooks/usePrevCurrentRatesBatch'

interface Props {
    baseCurrency: string
    info: SymbolInfo[]
    stakingCurrenciesMap: Set<string>
    rateConvert: RateConvert | null
    isNewRateService: boolean
    currencies: GetCurrenciesReturn | undefined
    filter?: MarketAssetFilter
    favoriteCoins?: Set<string>
    ratesData?: GetRatesBatchReturn
    prevRatesData?: GetRatesBatchReturn
    stakingUser?: StakingUser
    tickers?: ReturnType<typeof useTickerBatch>['data']
    withChart?: boolean
    searchInput?: string
}

const useBuildTableData = ({
    info,
    filter,
    favoriteCoins,
    stakingCurrenciesMap,
    rateConvert,
    ratesData,
    tickers,
    baseCurrency,
    stakingUser,
    isNewRateService,
    searchInput,
    withChart,
    currencies,
    prevRatesData,
}: Props) => {
    const ratesDataMap = useRatesDataMap({ isNewRateService, ratesData, withChart, currencies, prevRatesData })

    let data: CoinsTableRowData[] = []
    // eslint-disable-next-line complexity
    info.forEach(item => {
        if (filter?.type === 'favourites' && !favoriteCoins?.has(item.code)) {
            return
        }

        const hasStaking = stakingCurrenciesMap.has(item.code)

        if (filter) {
            if (filter.type === 'staking' && !hasStaking) {
                return
            }

            if (filter.symbols?.size && !isCoinInSymbolsFilter(filter.symbols, item)) {
                return
            }
        }

        let changePercent
        let volume
        let price
        let chart

        if (isNewRateService) {
            changePercent = ratesDataMap[item.code]?.changePercentage ?? null
            price = ratesDataMap[item.code]?.price ?? null
            volume = null
            chart = ratesDataMap[item.code]?.chart ?? null
        } else {
            const revertRateClose = rateConvert?.getRate(baseCurrency, item.code)
            const revertRateOpen = rateConvert?.getRate(baseCurrency, item.code, 'open')
            const rateClose = revertRateClose ? 1 / revertRateClose : null
            const rateOpen = revertRateOpen ? 1 / revertRateOpen : null

            const symbolTicker = tickers?.[item.symbolCode]

            if (rateOpen && rateClose) {
                const change = rateClose - rateOpen
                changePercent = (change / rateOpen) * 100
            }

            if (rateClose && symbolTicker) {
                if (item.isReverseSymbol) {
                    volume = Number(symbolTicker.q ?? 0) * rateClose
                } else {
                    volume = Number(symbolTicker.v ?? 0) * rateClose
                }
            }

            price = rateClose != null ? String(rateClose) : null
            changePercent = changePercent != null ? String(changePercent) : null
            volume = volume != null ? String(volume) : null
        }

        data.push({
            price,
            volume,
            changePercent,
            type: item.type,
            name: item.name,
            code: item.code,
            showStakingBadge: hasStaking,
            isBuyDisabled: item.isBuyDisabled,
            isStakingEnabled: stakingUser?.enabled,
            to: generateUrl({ route: Routes.asset, currencyRoute: item.code }),
            chart,
        })
    })

    data = searchInput ? data.filter(makeFilterByCodeName(searchInput)) : data

    return { data }
}

export const useData = (
    info: SymbolInfo[],
    filter: MarketAssetFilter | undefined,
    favoriteCoins = new Set<string>(),
    withChart = false,
    searchInput?: string | undefined
) => {
    const { data: currencies, isLoading: isCurrenciesLoading } = useCurrencies()
    const { data: cmsConfig, isLoading: isConfigLoading } = useCommonCMSConfig()
    const { showStaking, isNewRateService } = cmsConfig?.features || {}
    const { data: tickers, error: tickersError } = useTickerBatch({ symbols: ['*'], speed: '3s' })
    const { calculatedBaseCurrency: baseCurrency } = useBaseCurrency()
    const rateConvert = useRateConvert()

    const { data: stakingCurrencies, isLoading: isStakingLoading } = useStakingCurrencies()
    const { data: stakingUser, isLoading: isUserStakingLoading } = useStakingUser({ shouldRequest: showStaking })
    const stakingCurrenciesMap = useMemo(
        () => stakingCurrencies?.reduce((acc, item) => acc.add(item.currency), new Set<string>()) || new Set<string>(),
        [stakingCurrencies]
    )

    const {
        ratesData,
        prevRatesData,
        isLoading: isRatesLoading,
    } = usePrevCurrentRatesBatch({
        isNewRateService,
        currencies: Object.keys(currencies || {}),
        quote: baseCurrency,
    })

    const { data } = useBuildTableData({
        info,
        filter,
        tickers,
        ratesData,
        withChart,
        currencies,
        rateConvert,
        stakingUser,
        searchInput,
        baseCurrency,
        favoriteCoins,
        prevRatesData,
        stakingCurrenciesMap,
        isNewRateService: Boolean(isNewRateService),
    })

    return {
        data,
        isLoading:
            isConfigLoading ||
            isStakingLoading ||
            isUserStakingLoading ||
            isCurrenciesLoading ||
            (isNewRateService ? isRatesLoading : !tickers && !tickersError),
    }
}
